import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  Grid,
  Container,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Divider
} from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import {
  LockOutlined,
  PersonAddOutlined
} from '@mui/icons-material';
import { useAuth } from '../context/AuthProvider.tsx';
import { useRef, useEffect, useState } from "react";
import HomeSEO from "../components/HomeSEO.tsx"

const LoginPage = () => {
  const { login, user, signup } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))
  const controls = useAnimation();
  const showcaseRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  // Add scroll event listener to detect when page is scrolled
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Initialize on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Scroll to the app showcase section
  const scrollToShowcase = () => {
    if (showcaseRef.current) {
      showcaseRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    controls.start({
      opacity: 1, // Ensure final opacity is 100%
      y: 0,
      transition: { duration: 0.5 }
    });
  }, [controls]);

  return (
    <>
      <HomeSEO />
      <Box sx={{ minHeight: '100vh', backgroundColor: "#0A0F15", color: "antiquewhite", position: 'relative', overflow: 'hidden', scrollBehavior: "smooth" }}>

        {/* Top Banner */}
        <AppBar position="fixed" sx={{ background: scrolled ? "#0A0F15" : "none", boxShadow: 'none', borderBottom: !scrolled ? 'none' : '1px solid #3b3b3b', zIndex: 40 }}>
          <Toolbar>
            {/* Logo on the far left */}
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: isMobile ? 0 : isTablet ? 2 : 8 }}>
              <img
                src='/icon.png'
                height='30px'
                alt="TimeCapsule Logo"
                style={{ marginRight: "15px" }}
              />
              <img
                src='/text-logo2.png'
                height='40px'
                alt="TimeCapsule Logo"
                style={{}}
              />

            </Typography>

            {/* Login Button on the far right */}
            {!user && (
              <Button
                variant="contained"
                size='small'
                startIcon={<LockOutlined />}
                onClick={login}
                sx={{
                  py: 1,
                  px: isMobile ? 2 : 3,
                  borderRadius: 2,
                  fontSize: isMobile ? '0.6rem' : '1rem',
                  background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                  '&:hover': {
                    background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                    transition: 'all 0.3s ease-in-out'
                  },
                }}
              >
                Log In
              </Button>
            )}
          </Toolbar>
        </AppBar>

        {/* Hero Section */}
        <Container maxWidth="md" sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', scrollSnapAlign: 'start', mt: 6 }}>

          {/* Glow Effect with Ambient Motion */}
          <motion.div
            initial={{ opacity: 0.5, scale: 1 }}
            animate={{ opacity: [0.5, 0.7, 0.5], scale: [1, 1.05, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: 'absolute',
              top: 30,
              left: 0,
              width: '100%',
              height: '40%',
              minWidth: '100%',
              minHeight: '40%',
              borderRadius: '50%',
              background: 'rgba(123, 44, 191, 0.3)',
              filter: isMobile ? 'blur(80px)' : 'blur(150px)',
              zIndex: 0,
              overflow: 'visible'
            }}
          />

          {/* Animated Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            style={{ opacity: 1 }} // Ensure final opacity is 100%
          >
            <Typography component="h1" variant="h2" align="left" sx={{
              color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.5)', fontSize: { xs: '3rem', sm: '3rem', md: '4rem' },
              mb: 4, mt: 4, zIndex: 1
            }} gutterBottom>
              Capture <span style={{ color: '#7B2CBF', fontWeight: 'bold' }}>moments</span>, create future <span style={{ color: '#7B2CBF', fontWeight: 'bold' }}>surprises</span>.
            </Typography>
            <Typography variant="h5" align="left" sx={{
              color: 'rgba(255,255,255,0.9)', textShadow: '1px 1px 2px rgba(0,0,0,0.5)', fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              mb: 6, zIndex: 1
            }} paragraph>
              Begin your journey today and start creating memories that will surprise and delight in the future.
            </Typography>
          </motion.div>

          {/* Login/Signup Buttons */}
          {!user && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={controls}
              style={{ opacity: 1, zIndex: 1 }} // Ensure final opacity is 100%
            >
              <Box sx={{ display: 'flex', justifyContent: 'left', gap: 2, mb: 8 }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddOutlined />}
                    size="large"
                    onClick={signup}
                    sx={{
                      py: 1.5, 
                      px: 4,
                      borderRadius: 2,
                      fontSize: '1.1rem', 
                      bgcolor: '#7B2CBF', 
                      '&:hover': { bgcolor: '#9D4EDD' },
                    }}
                  >
                    Sign Up
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="outlined"
                    startIcon={<LockOutlined />}
                    size="large"
                    onClick={scrollToShowcase}
                    sx={{
                      py: 1.5, 
                      px: 4, 
                      fontSize: '1.1rem',
                      borderRadius: 2,
                      borderColor: '#7B2CBF', 
                      color: 'white', 
                      '&:hover': { borderColor: '#9D4EDD' },
                    }}
                  >
                    See More
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          )}
        </Container>

        {/* App Showcase Section */}
        <Container maxWidth="xl" sx={{ py: 8, mb: 3, scrollSnapAlign: 'start', zIndex: 1 }} ref={showcaseRef}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            style={{ opacity: 1 }} // Ensure final opacity is 100%
          >
            <Typography variant="h3" align="center" gutterBottom sx={{
              mb: 4,
              color: 'white',
              fontWeight: 400,
              letterSpacing: '0.02em',
              fontSize: { xs: '2rem', md: '2.7rem' }
            }}>
              Discover Time Capsule
            </Typography>
            <Typography variant="h5" align="center" sx={{
              mb: 3,
              color: 'white',
              fontWeight: 300,
              letterSpacing: '0.01em',
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}>
              Preserve your memories and create future surprises with Time Capsule.
            </Typography>
            <Typography variant="body1" align="center" sx={{
              mb: 6,
              color: 'rgba(255,255,255,0.85)',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              fontWeight: 300,
              lineHeight: 1.7,
              maxWidth: '900px',
              mx: 'auto',
              px: 2
            }}>
              Time Capsule is the perfect way to capture and share your most cherished moments. Whether it's a birthday, anniversary, or just a special day, our app makes it easy to create a time capsule that will surprise and delight in the future.
            </Typography>
            <Divider sx={{ color: 'white' }} />

            {/* Placeholder Images for App Screenshots - Removed hover effects, increased z-index */}
            <Grid container spacing={0} sx={{ justifyContent: 'center', mb: 4 }}>
              {[1, 2].map((index) => (
                <Grid item xs={6} sm={4} md={4} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box
                      component="img"
                      src={`/phone${index}${index}.png`}
                      alt={`App Screenshot ${index}`}
                      sx={{
                        maxHeight: isMobile ? '400px' : '600px',
                        borderRadius: '16px',
                        position: 'relative',
                        zIndex: 20
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={2} sx={{ p: 3, pb: 0 }}>
              {[0].map((index) => (
                <>
                  <Grid item xs={8} sm={8} md={8} key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      <Typography variant='h6' sx={{
                        display: 'flex',
                        background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 500,
                        letterSpacing: '0.01em',
                        fontSize: { md: '1.6rem' }
                      }}>
                        Set Your Unlock Date
                      </Typography>
                      <Typography sx={{
                        display: 'flex',
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: { md: '1.1rem' },
                        fontWeight: 300,
                        lineHeight: 1.7
                      }}>
                        Choose a time that tells your story - 5 years, 10 years or random dates for surprise nostalgia bursts.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} key={index}>
                    <Box
                      component="img"
                      src={`/phone${index}${index}.png`}
                      alt={`App Screenshot ${index}`}
                      sx={{
                        maxHeight: isMobile ? '160px' : '300px',
                        position: 'relative',
                        ml: isMobile ? 0 : 10,
                        zIndex: 20
                      }}
                    />
                  </Grid>
                  <Divider orientation="horizontal" variant="middle" sx={{ weight: 50, borderColor: 'white', width: '100%' }} />
                </>
              ))}
            </Grid>

            <Grid container spacing={2} sx={{ px: 3 }}>
              {[4].map((index) => (
                <>
                  <Grid item xs={4} sm={4} md={4} key={index}>
                    <Box
                      component="img"
                      src={`/phone${index}${index}.png`}
                      alt={`App Screenshot ${index}`}
                      sx={{
                        maxHeight: isMobile ? '150px' : '300px',
                        mt: '0px',
                        ml: isMobile ? 0 : 20,
                        position: 'relative',
                        zIndex: 20
                      }}
                    />
                  </Grid>
                  <Grid item xs={8} sm={8} md={8} key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant='h6' sx={{
                        display: 'flex',
                        background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 500,
                        letterSpacing: '0.01em',
                        ml: 2,
                        mr: -2,
                        fontSize: { md: '1.6rem' }
                      }}>
                        Celebrate Together
                      </Typography>
                      <Typography sx={{
                        display: 'flex',
                        color: 'rgba(255,255,255,0.9)',
                        ml: 2,
                        mr: -2,
                        fontSize: { md: '1.1rem' },
                        fontWeight: 300,
                        lineHeight: 1.7,
                        fontStyle: 'italic'
                      }}>
                        "We created a capsule with friends on our wedding day. Now we count down to our 1st anniversary reveal party!"
                      </Typography>
                    </Box>
                  </Grid>
                </>
              ))}
            </Grid>

            {/* Redesigned "Digital Meets Physical" section with modern typography */}
            <Divider orientation="horizontal" variant="middle" sx={{ borderColor: 'rgba(255,255,255,0.15)', mt: 5 }} />
            <Grid container spacing={4} sx={{ mt: 5, mb: 7 }}>
              <Grid item xs={12} sm={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: '600px', p: { md: 4 } }}>
                  <Typography
                    variant='h3'
                    sx={{
                      background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontWeight: 400,
                      letterSpacing: '0.005em',
                      mb: 4,
                      fontSize: { xs: '1.7rem', md: '2.5rem' },
                      position: 'relative'
                    }}
                  >
                    Digital Meets Physical
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      color: 'rgba(255,255,255,0.95)',
                      fontSize: { xs: '1rem', md: '1.3rem' },
                      fontWeight: 300,
                      letterSpacing: '0.01em',
                      lineHeight: 1.9,
                      mb: 3
                    }}
                  >
                    Transform your digital memories into beautiful, tangible treasures. Your selected TimeCapsule images can be printed and delivered to your door as Polaroid-style photos on the capsule unlock date.
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'rgba(255,255,255,0.75)',
                      fontSize: { xs: '0.9rem', md: '1.1rem' },
                      mt: 3,
                      fontStyle: 'italic',
                      fontWeight: 300,
                      position: 'relative'
                    }}
                  >
                    Preserve your most precious moments in a format you can hold, share, and display.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 30,
                    display: 'inline-block',
                    overflow: 'visible'
                  }}
                >
                  <Box
                    component="img"
                    src={`/pics2.png`}
                    alt={`Polaroid-style photos example`}
                    sx={{
                      height: isMobile ? '250px' : '400px',
                      maxWidth: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                      position: 'relative',
                      zIndex: 30
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        </Container>


        {/* Footer Section */}
        <Box component="footer" sx={{
          mt: 'auto',
          py: 3,
          color: 'white',
          textAlign: 'center',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Typography variant="body2" sx={{
            letterSpacing: '0.05em',
            fontWeight: 300,
            fontSize: '0.85rem'
          }}>
            © {new Date().getFullYear()} Time Capsule. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2 }}>
            <Typography
              component="a"
              onClick={() => navigate('/privacy')}
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.8rem',
                cursor: 'pointer',
                '&:hover': {
                  background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  transition: 'all 0.3s ease-in-out'
                }
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              component="a"
              onClick={() => navigate('/terms')}
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.8rem',
                cursor: 'pointer',
                '&:hover': {
                  background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  transition: 'all 0.3s ease-in-out'
                }
              }}
            >
              Terms of Service
            </Typography>
            <Typography
              component="a"
              onClick={() => navigate('/contact')}
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.8rem',
                cursor: 'pointer',
                '&:hover': {
                  background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  transition: 'all 0.3s ease-in-out'
                }
              }}
            >
              Contact Us
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;
